import React, { useEffect, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";

const CustomSurveyNav = ({ model, disabled = false, onFinish, disableFinish = false, finishLabel = "Terminar cuestionario" }) => {
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!model) return;

    const rerender = () => forceRender((v) => v + 1);

    model.onCurrentPageChanged.add(rerender);
    model.onCurrentPageChanging.add(rerender);
    model.onValueChanged.add(rerender);
    model.onPageVisibleChanged?.add(rerender);
    model.onVisibleChanged?.add(rerender);

    rerender();

    return () => {
      model.onCurrentPageChanged.remove(rerender);
      model.onCurrentPageChanging.remove(rerender);
      model.onValueChanged.remove(rerender);
      model.onPageVisibleChanged?.remove(rerender);
      model.onVisibleChanged?.remove(rerender);
    };
  }, [model]);

  if (!model) return null;

  const visiblePages = model.visiblePages ?? model.pages?.filter((p) => p.isVisible) ?? [];
  const visiblePageCount = visiblePages.length;

  const currentVisibleIndex = Math.max(
    0,
    visiblePages.findIndex((p) => p === model.currentPage),
  );

  const canPrev = currentVisibleIndex > 0;
  const isLastPage = currentVisibleIndex >= visiblePageCount - 1;

  const handlePrev = () => {
    if (!model || disabled || !canPrev) return;
    model.prevPage();
  };

  const handleNext = () => {
    if (!model || disabled) return;
    if (isLastPage) {
      onFinish?.();
    } else {
      model.nextPage();
    }
  };

  // Si solo hay 1 página y no hay callback de terminar, no renderiza nada
  if (visiblePageCount <= 1 && !onFinish) return null;

  const responsiveButtonSx = {
    fontSize: {
      xs: "var(--q-button-font-size, 0.78rem)",
      sm: "var(--q-button-font-size, 0.82rem)",
      md: "var(--q-button-font-size, 0.875rem)",
    },
    px: {
      xs: "var(--q-button-px, 12px)",
      sm: "var(--q-button-px, 14px)",
      md: "var(--q-button-px, 16px)",
    },
    py: {
      xs: "var(--q-button-py, 6px)",
      sm: "var(--q-button-py, 7px)",
      md: "var(--q-button-py, 8px)",
    },
    minHeight: {
      xs: "var(--q-button-min-height, 36px)",
      sm: "var(--q-button-min-height, 38px)",
      md: "var(--q-button-min-height, 40px)",
    },
    minWidth: {
      xs: "100%",
      sm: "auto",
    },
    lineHeight: 1.2,
    textTransform: "none",
    whiteSpace: "normal",
    textAlign: "center",
  };

  return (
    <Box sx={{ mt: 2, width: "100%" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        spacing={{ xs: 1.5, sm: 2 }}
        sx={{ width: "100%" }}
      >
        {visiblePageCount > 1 ? (
          <Typography
            className="custom-survey-nav-text"
            sx={{
              fontSize: "var(--q-small-text-size, 0.875rem)",
              lineHeight: 1.3,
            }}
          >
            Página {currentVisibleIndex + 1} de {visiblePageCount}
          </Typography>
        ) : (
          <Box />
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="flex-end"
          sx={{
            width: { xs: "100%", sm: "auto" },
            alignSelf: { xs: "stretch", sm: "center" },
          }}
        >
          {visiblePageCount > 1 && (
            <Button variant="outlined" onClick={handlePrev} disabled={disabled || !canPrev} sx={responsiveButtonSx}>
              Anterior
            </Button>
          )}

          <Button
            variant="contained"
            color="primary"
            onClick={handleNext}
            disabled={disabled || (isLastPage ? disableFinish : false)}
            sx={responsiveButtonSx}
          >
            {isLastPage ? finishLabel : "Siguiente"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CustomSurveyNav;
